import {
  type DefinirCuidador,
  MENSAJE_TELEFONO_INVALIDO,
  normalizarTelefonoAr,
} from '@pasos/schemas';
import { type FormEvent, useState } from 'react';
import { CampoGrande } from '../components/domain/CampoGrande.js';
import { Boton } from '../components/ui/Boton.js';
import { ArrowRight, Eye, EyeOff, ICONO, type Icono, Smartphone } from '../components/ui/iconos.js';
import { PACIENTE } from '../copy/index.js';
import { Stepper, TituloDelPaso } from './Stepper.js';

type PasoCuidadorProps = {
  onDefinir: (decision: DefinirCuidador) => void;
  enviando: boolean;
  error?: string | undefined;
};

/**
 * P-02c · contacto de apoyo, opcional.
 *
 * La card de "qué ve y qué no" no es relleno: es la pieza que sostiene la
 * confianza. Sumar a un familiar a una app de salud es una decisión que la
 * persona sólo toma si entiende el límite, y el límite del cuidador es
 * intencionalmente acotado (C-00 a C-05) — acompaña, no accede a lo clínico.
 *
 * Saltear está al mismo nivel visual que continuar. Esconderlo convertiría un
 * paso opcional en uno obligatorio de hecho.
 *
 * E3.8.2, contra la referencia P-02c: lo que el cuidador ve y no ve **sube arriba
 * de los campos** y pasa a dos bloques con ícono de ojo. Es la decisión de
 * privacidad más importante del onboarding y antes era texto corrido al final.
 */

function QueVe({
  titulo,
  items,
  icono: IconoDelItem,
  tono,
}: {
  titulo: string;
  items: string[];
  icono: Icono;
  tono: 'si' | 'no';
}) {
  return (
    <div
      className={`flex flex-col gap-2 rounded-lg p-4 ${
        tono === 'si' ? 'bg-accent-soft text-heading' : 'bg-muted text-body'
      }`}
    >
      <h2 className="m-0 text-sm font-bold tracking-widest uppercase">{titulo}</h2>
      <ul className="m-0 flex list-none flex-col gap-2 p-0">
        {items.map((item) => (
          <li className="flex items-start gap-2 text-md" key={item}>
            <IconoDelItem aria-hidden="true" className={`${ICONO.normal} mt-0.5 shrink-0`} />
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}
export function PasoCuidador({ onDefinir, enviando, error }: PasoCuidadorProps) {
  const [relacion, setRelacion] = useState('');
  const [telefono, setTelefono] = useState('');
  const [tocado, setTocado] = useState(false);

  const quiereInvitar = relacion.trim() !== '' || telefono.trim() !== '';

  // La misma función que corre en el servidor (`telefonoArSchema`). Validar acá
  // es cortesía —el error se ve al instante y no después de un viaje de red—;
  // la garantía es la del endpoint, que no confía en lo que llega del navegador.
  const telefonoNormalizado = normalizarTelefonoAr(telefono);

  const faltaRelacion = tocado && quiereInvitar && relacion.trim().length < 2;
  const telefonoInvalido = tocado && quiereInvitar && telefonoNormalizado === null;

  function enviar(evento: FormEvent) {
    evento.preventDefault();
    setTocado(true);
    if (!quiereInvitar) return;
    if (relacion.trim().length < 2 || !telefonoNormalizado) return;
    // Viaja el normalizado, no lo tipeado: es lo que va a recibir WhatsApp (E5).
    onDefinir({ accion: 'invitar', relacion: relacion.trim(), telefono: telefonoNormalizado });
  }

  return (
    <section className="flex flex-col">
      <Stepper paso={3} />

      <div className="flex flex-col gap-5 px-5 py-6">
        <div>
          <TituloDelPaso>{PACIENTE.onboarding.cuidador.titulo}</TituloDelPaso>
          <p className="m-0 mt-1 text-lg text-body">{PACIENTE.onboarding.cuidador.presentacion}</p>
        </div>

        <QueVe
          icono={Eye}
          items={[PACIENTE.onboarding.cuidador.veEstado, PACIENTE.onboarding.cuidador.veAvisos]}
          titulo={PACIENTE.onboarding.cuidador.veTitulo}
          tono="si"
        />
        <QueVe
          icono={EyeOff}
          items={[
            PACIENTE.onboarding.cuidador.noVeConversaciones,
            PACIENTE.onboarding.cuidador.noVeClinico,
          ]}
          titulo={PACIENTE.onboarding.cuidador.noVeTitulo}
          tono="no"
        />

        <form className="flex flex-col gap-5" noValidate onSubmit={enviar}>
          <CampoGrande
            ayuda={PACIENTE.onboarding.cuidador.vinculoAyuda}
            error={faltaRelacion ? PACIENTE.onboarding.cuidador.vinculoFalta : undefined}
            etiqueta={PACIENTE.onboarding.cuidador.vinculoEtiqueta}
            name="relacion"
            onChange={(e) => setRelacion(e.target.value)}
            value={relacion}
          />

          <CampoGrande
            ayuda={PACIENTE.onboarding.cuidador.celularAyuda}
            error={
              telefonoInvalido
                ? telefono.trim() === ''
                  ? PACIENTE.onboarding.cuidador.celularFalta
                  : MENSAJE_TELEFONO_INVALIDO
                : undefined
            }
            etiqueta={PACIENTE.onboarding.cuidador.celularEtiqueta}
            icono={Smartphone}
            inputMode="tel"
            name="telefono"
            onChange={(e) => setTelefono(e.target.value)}
            type="tel"
            value={telefono}
          />

          {error ? (
            <p className="m-0 text-md font-bold text-destructive-fg" role="alert">
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-2">
            <Boton cargando={enviando} disabled={!quiereInvitar} type="submit">
              {PACIENTE.onboarding.continuar}
              <ArrowRight aria-hidden="true" className={ICONO.normal} />
            </Boton>
            <Boton
              cargando={false}
              onClick={() => onDefinir({ accion: 'saltear' })}
              variante="texto"
            >
              {PACIENTE.onboarding.cuidador.saltar}
            </Boton>
          </div>
        </form>
      </div>
    </section>
  );
}
